import { useSnackbar } from 'notistack'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { FilterModel } from '@omnea-app/legacy/utils/savedFilters/validateFilterModel'
import { useCreateTableFilter } from '../../table-filters/useTableFilters'
import { useSavedFilterContext } from './SavedFilterContext'
import { SavedFilterSettings, SavedFilterSettingsDialog } from './SavedFilterSettingsDialog'

interface CreateSavedFilterDialogProps {
  isOpen: boolean
  onClose(): void
  filterModel: FilterModel
}

export const CreateSavedFilterDialog: React.FC<CreateSavedFilterDialogProps> = ({ isOpen, onClose, filterModel }) => {
  const { reference } = useSavedFilterContext()
  const { t } = useTranslation('requests')
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useAuthenticatedUser()

  const { mutateAsync: createTableFilter } = useCreateTableFilter()

  async function handleSubmit(settings: SavedFilterSettings) {
    if (!reference) {
      console.error('No reference provided for saved filter, perhaps you forgot to wrap the table in a SavedFilterContext?')
      enqueueSnackbar(t('create-user-request-filter.dialog.create-error'), { variant: 'error' })
      return
    }

    // Makes sure that the filter model has unique ids.
    // This is because sometimes user will create saved filters from someone else's saved filter.
    const newFilterModel = assignNewIdsToFilterModel(filterModel)

    try {
      const result = await createTableFilter({
        name: settings.name,
        isPublic: settings.isPublic,
        filters: newFilterModel,
        reference,
      })

      if (!result?.id) {
        enqueueSnackbar(t('create-user-request-filter.dialog.create-error'), { variant: 'error' })
        return
      }

      enqueueSnackbar(t('create-user-request-filter.dialog.create-success'), { variant: 'success' })
      onClose()
    } catch (error) {
      console.error('Failed to create table filter', { error, settings, userId: user?.id })
      enqueueSnackbar(t('create-user-request-filter.dialog.create-error'), { variant: 'error' })
    }
  }

  return (
    <SavedFilterSettingsDialog
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      displayVisibilityDropdown={user.isAdmin}
      key={JSON.stringify(filterModel)}
    />
  )
}

const assignNewIdsToFilterModel = (filterModel: FilterModel): FilterModel => {
  const items = filterModel.items.map((item) => ({ ...item, id: v4() }))

  return { ...filterModel, items }
}
